/* eslint-disable no-script-url,jsx-a11y/anchor-is-valid */
import React, { useEffect, useState } from "react";
import Dropdown from "react-bootstrap/Dropdown";
import Modal from "react-bootstrap/Modal";
import MyInvitations from "../../../../components/MyInvitations";
import { getMyInvitations } from "../../../../app/UrlCrud";
import { useSelector, useDispatch } from "react-redux";
import * as auth from "../../../../app/modules/Auth/_redux/authRedux";
import {
  getUserCompanies,
  switchCompany,
  removeFromCompany
} from "../../../../app/modules/Auth/_redux/authCrud";
import KTLayoutAsideMenu from "../../../_assets/js/layout/base/aside-menu";

// Lets a user who belongs to more than one company switch which company's
// data they're currently viewing across the dashboard.
export function CompanySwitcher() {
  const dispatch = useDispatch();
  const { user } = useSelector(state => state.auth);
  const currentUser = user && user.user;

  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [invitationCount, setInvitationCount] = useState(0);
  const [showInvitations, setShowInvitations] = useState(false);

  const load = () => {
    if (!currentUser || !currentUser._id) return;

    // Pending invitations to other organisations (nothing is joined until accepted)
    getMyInvitations()
      .then(({ data }) => setInvitationCount((data || []).length))
      .catch(() => setInvitationCount(0));

    getUserCompanies()
      .then(({ data }) => setCompanies(data || []))
      .catch(() => setCompanies([]));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser && currentUser._id, currentUser && currentUser.activeCompany]);

  // This component mounts/unmounts (renders null below 2 companies) AFTER
  // the aside menu's scrollable-height calculation already ran on layout
  // init - that calculation only reserves space for THIS component if it's
  // already present in the DOM at that moment (see aside-menu.js's
  // `scroll.height` function, which checks for #kt_aside_company_switcher).
  // Since this fetch is async, force the aside menu to recompute its height
  // once we know whether this component will actually render anything, so
  // the scrollable menu correctly shrinks to leave room for this switcher
  // instead of covering/pushing it below the viewport.
  useEffect(() => {
    const menu = KTLayoutAsideMenu.getMenu();
    if (menu) {
      // Wait a tick for this component's own DOM (or lack thereof) to commit
      // first. Must call `scrollReInit()` (which re-runs the scroll area's
      // `height` function and re-applies the computed height), NOT
      // `scrollUpdate()` - the latter only refreshes the scrollbar
      // thumb/track for the CURRENT (stale, already-applied) height, it does
      // not recompute or re-apply the height itself.
      setTimeout(() => menu.scrollReInit(), 0);
    }
  }, [companies.length, invitationCount]);

  // Nothing to switch between and nothing to answer - don't clutter the sidebar.
  const canSwitch = companies && companies.length >= 2;
  if (!canSwitch && invitationCount === 0) return null;

  const invitationsModal = (
    <Modal show={showInvitations} onHide={() => setShowInvitations(false)} centered>
      <Modal.Header closeButton>
        <Modal.Title>Organisation invitations</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <MyInvitations
          onChanged={list => setInvitationCount(list.length)}
          onAccepted={() => window.location.reload()}
        />
      </Modal.Body>
    </Modal>
  );

  if (!canSwitch) {
    return (
      <div id="kt_aside_company_switcher" className="px-5 py-3 border-top">
        <button type="button" className="btn btn-light-primary w-100" onClick={() => setShowInvitations(true)}>
          Invitations ({invitationCount})
        </button>
        {invitationsModal}
      </div>
    );
  }

  const active = companies.find(c => c.isActive) || companies[0];

  const handleSwitch = async company => {
    if (loading || company.isActive || company.disabled) return;

    setLoading(true);
    try {
      await switchCompany(company.isHome ? null : company.company._id);
      // A plain redux user refresh only updates the sidebar/permissions -
      // every page's own data comes from its own axios calls in a
      // mount-only useEffect, which never re-runs just because redux
      // state changed. A full reload re-fetches BOTH the user context AND
      // whatever the current page renders, against the newly-active
      // company - the same effect the user was previously getting by
      // manually refreshing after switching, just automatic.
      window.location.reload();
    } catch (e) {
      console.log("Error switching company", e);
      setLoading(false);
    }
  };

  const handleLeave = async company => {
    if (loading) return;
    if (
      !window.confirm(
        `Leave ${company.company.name}? You'll need to be re-added to switch back.`
      )
    )
      return;

    setLoading(true);
    try {
      await removeFromCompany(company.company._id);
      if (company.isActive) {
        dispatch(auth.actions.requestUser());
      }
      load();
    } catch (e) {
      console.log("Error leaving company", e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div id="kt_aside_company_switcher" className="px-5 py-3 border-top">
      <Dropdown drop="up">
        <Dropdown.Toggle
          variant="light"
          className="w-100 text-left d-flex align-items-center justify-content-between"
          id="dropdown-company-switcher"
          disabled={loading}
        >
          <span className="font-weight-bold text-truncate d-inline-block" style={{ maxWidth: "85%" }}>
            {active && active.company ? active.company.name : "Select company"}
          </span>
        </Dropdown.Toggle>
        <Dropdown.Menu className="w-100">
          {companies.map(c => (
            <div
              key={c.company._id}
              className="d-flex align-items-center px-4 py-2 dropdown-item-company"
            >
              <span
                className="flex-grow-1 text-truncate"
                style={{
                  cursor: c.disabled ? "not-allowed" : "pointer",
                  opacity: c.isActive ? 0.6 : 1
                }}
                onClick={() => handleSwitch(c)}
              >
                {c.company.name}
                {c.isHome ? " (Home)" : ""}
                {c.disabled ? " - disabled" : ""}
                {c.isActive ? " ✓" : ""}
              </span>
              {!c.isHome && (
                <button
                  type="button"
                  title="Leave company"
                  className="btn btn-xs btn-icon btn-light-danger ml-2"
                  disabled={loading}
                  onClick={e => {
                    e.stopPropagation();
                    handleLeave(c);
                  }}
                >
                  &times;
                </button>
              )}
            </div>
          ))}
          {invitationCount > 0 && (
            <div className="px-4 py-2 border-top">
              <button type="button" className="btn btn-sm btn-light-primary w-100" onClick={() => setShowInvitations(true)}>
                Invitations ({invitationCount})
              </button>
            </div>
          )}
        </Dropdown.Menu>
      </Dropdown>
      {invitationsModal}
    </div>
  );
}

